import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { createRouter, RouterProvider } from '@tanstack/react-router';

import '@/styles/globals.css';
import '@/lib/i18n';

import { MarketProvider } from '@/lib/market/MarketProvider';
import { createQueryClient } from '@/lib/query-client';
import { parseSearch, stringifySearch } from '@/lib/router/search-serializer';
import { ThemeProvider } from '@/lib/theme/ThemeProvider';
import { routeTree } from './routeTree.gen';

/**
 * =============================================================================
 *  組裝順序是有意義的，不是隨便疊的：
 *
 *    ThemeProvider          → 寫 documentElement.dataset.theme
 *      MarketProvider       → 寫 documentElement.dataset.market（**唯一**寫入點）
 *        RouterProvider     → URL 是 as_of / symbol / range 的權威
 *          QueryClientProvider（在 __root.tsx 裡）
 *
 *  Query 在 Router **之內**，因為每一個 query key 都要帶 as_of，而 as_of 住在 URL。
 *  反過來疊的話，QueryClientProvider 拿不到路由狀態，最後一定會有人在某個
 *  元件裡自己讀 window.location——那就是 as_of 開始漏掉的第一個地方。
 * ========================================================================== */

const queryClient = createQueryClient();

const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultPreload: 'intent',
  // §10.2 規則 2：hover 預取。120ms debounce 讓「滑過」與「要去」分得開。
  defaultPreloadDelay: 120,
  defaultPreloadStaleTime: 0,
  scrollRestoration: true,
  // 分享連結必須長得跟人手打的一樣：`?symbol=2330`，不是 `?symbol=%222330%22`。
  // 見 lib/router/search-serializer.ts（含「讀回來會是數字」那一段的後果）。
  parseSearch,
  stringifySearch,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

const container = document.getElementById('root');
if (container === null) {
  throw new Error('#root 不存在——index.html 被改壞了。');
}

createRoot(container).render(
  <StrictMode>
    <ThemeProvider>
      <MarketProvider>
        <RouterProvider router={router} />
      </MarketProvider>
    </ThemeProvider>
  </StrictMode>,
);
